import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ClipboardCheck, Rows3, LayoutGrid } from "lucide-react";

import { ACHIEVEMENT_CATEGORIES } from "../lib/successCriteria";
import { markdownComponents } from "./LessonDocument";

/**
 * Toggleable Success Criteria block.
 *
 * Renders the original markdown table by default (Table view). When the
 * teacher clicks "Achievement Chart view", swaps to a 4-card grouped
 * layout — one card per Achievement Chart category — with each
 * card listing its criteria and Level 3 descriptors. The mapping
 * helps unit-end report card writing because teachers see exactly
 * which Ministry category each criterion lives in.
 *
 * If a teacher has no criteria for a given category, the card is
 * rendered in a muted "Not covered in this lesson" state so the
 * Achievement Chart picture stays complete.
 */
export default function SuccessCriteriaSection({
  headingText,
  rawTable,
  criteria,
}) {
  const [view, setView] = useState("table"); // 'table' | 'chart'

  return (
    <section className="my-6" data-testid="success-criteria-section">
      <div className="flex flex-wrap items-center justify-between gap-2 mt-10 pb-2 border-b border-[#4a6984]/15">
        <h2 className="font-extrabold text-2xl text-[#4a6984] tracking-tight">
          {headingText || "Success Criteria"}
        </h2>
        <div
          className="inline-flex rounded-lg border border-[#4a6984]/20 bg-white/70 p-0.5 text-xs font-bold"
          data-testid="success-criteria-toggle"
          role="tablist"
        >
          <button
            type="button"
            onClick={() => setView("table")}
            data-testid="success-criteria-toggle-table"
            role="tab"
            aria-selected={view === "table"}
            className={`px-2.5 py-1.5 rounded-md transition-colors flex items-center gap-1.5 ${
              view === "table"
                ? "bg-[#1c2d3d] text-white"
                : "text-[#4a6984] hover:bg-[#96ded1]/25"
            }`}
          >
            <Rows3 className="h-3.5 w-3.5" />
            Table view
          </button>
          <button
            type="button"
            onClick={() => setView("chart")}
            data-testid="success-criteria-toggle-chart"
            role="tab"
            aria-selected={view === "chart"}
            className={`px-2.5 py-1.5 rounded-md transition-colors flex items-center gap-1.5 ${
              view === "chart"
                ? "bg-[#1c2d3d] text-white"
                : "text-[#4a6984] hover:bg-[#96ded1]/25"
            }`}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Achievement Chart view
          </button>
        </div>
      </div>

      {view === "table" ? (
        <div data-testid="success-criteria-table-view">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {rawTable}
          </ReactMarkdown>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5"
          data-testid="success-criteria-chart-view"
        >
          {ACHIEVEMENT_CATEGORIES.map((cat) => {
            const rows = criteria.filter((c) => c.category === cat.key);
            const hasRows = rows.length > 0;
            return (
              <article
                key={cat.key}
                className={`rounded-xl border p-4 ${
                  hasRows
                    ? "border-[#4a6984]/20 bg-white"
                    : "border-dashed border-[#4a6984]/15 bg-white/50"
                }`}
                data-testid={`success-criteria-chart-card-${cat.key
                  .replace(/&/g, "and")
                  .replace(/\s+/g, "-")
                  .toLowerCase()}`}
              >
                <header className="flex items-center gap-2 mb-3">
                  <ClipboardCheck
                    className={`h-4 w-4 ${
                      hasRows ? "text-[#1c2d3d]" : "text-[#4a6984]/45"
                    }`}
                  />
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/70">
                    Achievement Chart
                  </p>
                </header>
                <h3
                  className={`font-black text-lg tracking-tight mb-3 ${
                    hasRows ? "text-[#1c2d3d]" : "text-[#4a6984]/60"
                  }`}
                >
                  {cat.label}
                </h3>
                {hasRows ? (
                  <ul className="space-y-3.5">
                    {rows.map((row, i) => (
                      <li key={i} className="space-y-1.5">
                        <p className="text-[13px] font-bold text-[#1c2d3d] leading-snug">
                          {row.criterion}
                        </p>
                        {row.level3 && (
                          <p className="text-[11px] text-[#4a6984]/85 leading-snug">
                            <span className="font-bold uppercase tracking-[0.14em] text-[10px] text-[#4a6984]/65 mr-1">
                              Level 3:
                            </span>
                            {row.level3}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-[#4a6984]/55 italic">
                    Not covered in this lesson.
                  </p>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
